import { HxObjectID, HxObjectType } from '@outlook/hxcore_web';
import { createLocalId } from './localId';
import { createCompositeId } from './compositeId';
import { HxIdMappingInfo } from './hxIdMappingInfo';
import { trace } from 'owa-trace';
import find from 'lodash-es/find';
import { logUsage } from 'owa-analytics';

/**
 * map back and forth between graphql IDs and hx IDs
 */

let mapGraphqlToHx: Map<string, string> = new Map();
let mapHxToGraphql: Map<string, string> = new Map();

function addIdMappingInternal(graphqlId: string, hxObjectId: HxObjectID) {
    const hxId = hxObjectId.toString();
    let current = mapGraphqlToHx.get(graphqlId);
    if (current && current !== hxId) {
        // We expect this relationship to be 1:1 and never change
        const currentType = HxObjectType[new HxObjectID(current).getObjectType()];
        const newType = HxObjectType[hxObjectId.getObjectType()];

        trace.warn(`[HxIdMap] hxId changed old type: ${currentType} new type: ${newType}`);
        // Add telemetry to help us track down any cases where two different
        // hx object types trying to map to the same server id
        if (currentType !== newType) {
            logUsage('hxIdMap_typeCollision', {
                currentType,
                newType,
            });
        }
    }
    mapGraphqlToHx.set(graphqlId, hxId);

    current = mapHxToGraphql.get(hxId);
    if (current && current !== graphqlId) {
        // We expect this relationship to be 1:1 and never change
        trace.warn(`[HxIdMap] graphqlId changed type: ${hxObjectId.getObjectType()}`);
    }
    mapHxToGraphql.set(hxId, graphqlId);

    checkMapSize();
}

/**
 * This should be called via active set trackin. Manual calls are not expected.
 */
export function addIdMapping(hxIdMappingInfo: HxIdMappingInfo | null) {
    if (!hxIdMappingInfo) {
        return;
    }

    const { hxObjectId, serverId, context } = hxIdMappingInfo;
    let graphqlId;
    if (serverId && serverId.length > 0) {
        graphqlId = context ? createCompositeId(serverId, context) : serverId;
    } else {
        graphqlId = createLocalId(hxObjectId.toString());
    }
    addIdMappingInternal(graphqlId, hxObjectId);
    return;
}

/**
 * This should be called via active set trackin. Manual calls are not expected.
 */
export function removeIdMapping(hxId: string) {
    const graphqlId = mapHxToGraphql.get(hxId);
    if (graphqlId) {
        mapGraphqlToHx.delete(graphqlId);
        mapHxToGraphql.delete(hxId);
    }
    releaseReferences(hxId);
}

/**
 * Send telemetry if the map gets unreasonably large.
 */
let maxSizeForTelemetry = 10000;
function checkMapSize() {
    if (mapGraphqlToHx.size > maxSizeForTelemetry) {
        // report again if size continues to grow, but double the threshold
        // each time so we don't report too frequently.
        maxSizeForTelemetry = 2 * mapGraphqlToHx.size;
        const mapStats = calculateMapStats();
        trace.warn(`[HxIdMap] map too big size ${mapGraphqlToHx.size} ${mapStats}`);
    }
}

function calculateMapStats() {
    const countByType: Map<number, number> = new Map();
    mapGraphqlToHx.forEach((hxId, graphqlId) => {
        const hxObjectId = new HxObjectID(hxId);
        const type = hxObjectId.getObjectType();
        const count = countByType.get(type) ?? 0;
        countByType.set(type, count + 1);
    });
    let report = 'HxIdMap report';
    countByType.forEach((count, type) => {
        report = report + ` type ${type} count ${count}`;
    });
    return report;
}

export function getHxIdFromGraphqlId(graphqlId: string): HxObjectID | null {
    const hxId = mapGraphqlToHx.get(graphqlId);
    if (!hxId) {
        trace.warn(`Can't find hxId for ${graphqlId}`);
        return null;
    }

    return new HxObjectID(hxId);
}

export function getGraphqlIdFromHxId(hxObjectId: HxObjectID): string | null {
    const hxId = hxObjectId.toString();
    const graphqlId = mapHxToGraphql.get(hxId);
    if (!graphqlId) {
        trace.warn(`Can't find graphqlId for ${hxId}`);
        return null;
    }

    return graphqlId;
}

/**
 * Map from source hx ID to the set of other hx IDs it references.
 * An expected example usage would be that a conversation header
 * references the conversation object which isn't loaded
 * while showing the list, but needs to be mapped for the case
 * of loading reading pane content.
 */
let mapSourceToReferencedId: Map<string, string[]> = new Map();

/**
 * Map from referenced hx ID to count of references.
 * An example case requiring ref counting is the conversation header
 * to conversation reference. Since a conversation can appear in multiple
 * folders, there may be many conversation headers referencing the same
 * conversation and we need to keep the conversation in the id map
 * until the last referencing conversation header is dropped.
 */
let referencedIdRefCount: Map<string, number> = new Map();

function addRef(hxId: string) {
    const count = referencedIdRefCount.get(hxId) ?? 0;
    referencedIdRefCount.set(hxId, count + 1);
}

function releaseRef(hxId: string) {
    const count = referencedIdRefCount.get(hxId);
    if (!count) {
        return;
    }

    if (count > 1) {
        referencedIdRefCount.set(hxId, count - 1);
    } else {
        referencedIdRefCount.delete(hxId);
        removeIdMapping(hxId);
    }
}

export function addReferencedId(sourceHxId: HxObjectID, hxIdMappingInfo: HxIdMappingInfo) {
    const sourceId = sourceHxId.toString();
    addIdMapping(hxIdMappingInfo);
    let referencedHxIds = mapSourceToReferencedId.get(sourceId);
    if (!referencedHxIds) {
        referencedHxIds = [];
        mapSourceToReferencedId.set(sourceId, referencedHxIds);
    }

    const referencedHxId = hxIdMappingInfo.hxObjectId.toString();
    if (!find(referencedHxIds, id => id === referencedHxId)) {
        referencedHxIds.push(referencedHxId);
        addRef(referencedHxId);
    }
}

function releaseReferences(sourceHxId: string) {
    const referencedHxIds = mapSourceToReferencedId.get(sourceHxId);
    if (!referencedHxIds) {
        return;
    }

    referencedHxIds.forEach(referencedHxId => releaseRef(referencedHxId));
}

export function resetMapForTests() {
    mapGraphqlToHx = new Map();
    mapHxToGraphql = new Map();
    mapSourceToReferencedId = new Map();
    referencedIdRefCount = new Map();
}

// exposed for testing
export function getMapSize() {
    return mapHxToGraphql.size;
}
